import { useState, useEffect } from 'react';
import { signOut } from 'aws-amplify/auth';
import { fetchGameHistory } from '../api/profile/fetchGameHistory';
import {
    LineChart, Line,
    BarChart, Bar,
    XAxis, YAxis,
    CartesianGrid, Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { updateUserInfo } from '../api/profile/updateUserInfo';
import AWS from 'aws-sdk';

const formatChartDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CHART_GRID_COLOR = '#374151';
const CHART_AXIS_COLOR = '#6b7280';
const CHART_TICK_STYLE = { fill: '#9ca3af', fontSize: 12 };
const CHART_TOOLTIP_STYLE = { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8 };

function PerformanceSection({ userData }) {
    const [historyLoading, setHistoryLoading] = useState(true);
    const [visionHistory, setVisionHistory] = useState([]);
    const [colorGuesserHistory, setColorGuesserHistory] = useState([]);

    useEffect(() => {
        if (!userData?.id) return;
        setHistoryLoading(true);
        fetchGameHistory(userData.id).then((data) => {
            setVisionHistory((data.visionHistory || []).map((d) => ({ ...d, dateLabel: formatChartDate(d.date) })));
            setColorGuesserHistory((data.colorGuesserHistory || []).map((d) => ({ ...d, dateLabel: formatChartDate(d.date) })));
            setHistoryLoading(false);
        });
    }, [userData?.id]);

    const noVisionData = !historyLoading && visionHistory.length === 0;
    const noColorData = !historyLoading && colorGuesserHistory.length === 0;

    const emptyState = (
        <div className='flex flex-col justify-center items-center text-base-content opacity-50' style={{ height: 220 }}>
            <span style={{ fontSize: 48 }}>♟</span>
            <p className='mt-3 text-sm'>Play some games to see your progress here</p>
        </div>
    );

    const spinner = (color = 'primary') => (
        <div className='flex justify-center items-center' style={{ height: 220 }}>
            <span className={`loading loading-spinner loading-lg text-${color}`}></span>
        </div>
    );

    return (
        <div className="bg-base-200 rounded-2xl shadow p-6 w-full max-w-2xl">
            <h2 className="text-lg font-bold text-base-content mb-6">Performance</h2>

            <h3 className='text-primary font-semibold mb-3'>Vision Score Over Time</h3>
            {historyLoading ? spinner('primary') : noVisionData ? emptyState : (
                <ResponsiveContainer width='100%' height={220}>
                    <LineChart data={visionHistory} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                        <XAxis dataKey="dateLabel" stroke={CHART_AXIS_COLOR} tick={CHART_TICK_STYLE} />
                        <YAxis stroke={CHART_AXIS_COLOR} tick={CHART_TICK_STYLE} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={{ color: '#9ca3af' }} itemStyle={{ color: '#e5e7eb' }} formatter={(v, n) => [v, n === 'score' ? 'Score' : 'Accuracy %']} />
                        <Line type="monotone" dataKey="score" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: '#f97316' }} activeDot={{ r: 6 }} name="score" />
                        <Line type="monotone" dataKey="accuracy" stroke="#a78bfa" strokeWidth={2} dot={{ r: 4, fill: '#a78bfa' }} activeDot={{ r: 6 }} name="accuracy" />
                    </LineChart>
                </ResponsiveContainer>
            )}

            <h3 className='text-secondary font-semibold mt-6 mb-3'>Color Guesser Score Over Time</h3>
            {historyLoading ? spinner('secondary') : noColorData ? emptyState : (
                <ResponsiveContainer width='100%' height={220}>
                    <BarChart data={colorGuesserHistory} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_COLOR} />
                        <XAxis dataKey="dateLabel" stroke={CHART_AXIS_COLOR} tick={CHART_TICK_STYLE} />
                        <YAxis stroke={CHART_AXIS_COLOR} tick={CHART_TICK_STYLE} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={{ color: '#9ca3af' }} itemStyle={{ color: '#e5e7eb' }} formatter={(v) => [v, 'Score']} />
                        <Bar dataKey="score" fill="#a78bfa" radius={[4, 4, 0, 0]} name="score" />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

const S3_BUCKET = 'sixtyfour-public-assets';
const REGION = 'us-east-1';

AWS.config.update({
    accessKeyId: process.env.REACT_APP_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_SECRET_ACCESS_KEY,
});

const myBucket = new AWS.S3({
    params: { Bucket: S3_BUCKET },
    region: REGION,
});

function StatCard({ label, value, accent }) {
    const accentClass = accent === 'secondary' ? 'text-secondary' : 'text-primary';
    return (
        <div className="bg-base-300 rounded-xl p-4 flex flex-col items-center justify-center shadow">
            <span className={`text-2xl font-bold ${accentClass}`}>{value ?? '—'}</span>
            <span className="text-sm text-base-content/60 mt-1 text-center">{label}</span>
        </div>
    );
}

function EditModal({ userData, onSave, onClose }) {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleFileChange = (e) => {
        setUploadedFile(e.target.files[0]);
    };

    const handleSave = async () => {
        setSaving(true);
        await onSave({ uploadedFile });
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-base-200 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 flex flex-col gap-6">
                <h2 className="text-xl font-bold text-base-content">Edit Profile</h2>

                {/* Avatar preview */}
                <div className="flex flex-col items-center gap-3">
                    <div className="avatar">
                        <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                            <img
                                src={
                                    uploadedFile
                                        ? URL.createObjectURL(uploadedFile)
                                        : userData.picture
                                }
                                alt="avatar"
                            />
                        </div>
                    </div>
                    <label className="btn btn-ghost btn-sm gap-2 cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload Photo
                        <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                    </label>
                </div>

                <div className="divider my-0"></div>

                {/* Username field */}
                <div className="form-control gap-1">
                    <label className="label py-0">
                        <span className="label-text font-medium">Username</span>
                        <span className="label-text-alt text-base-content/50">3–20 characters</span>
                    </label>
                    <input
                        id="profileNameChange"
                        type="text"
                        placeholder={userData.profileName || 'Enter a username'}
                        className="input input-bordered focus:input-primary w-full"
                        maxLength={20}
                    />
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-2">
                    <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <span className="loading loading-spinner loading-sm"></span> : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Profile({ userData, userStats, setRefreshTrigger, onSignOut }) {
    const [editOpen, setEditOpen] = useState(false);

    const handleSignout = async () => {
        try {
            await signOut();
            if (onSignOut) onSignOut();
            else window.location.href = '/login';
        } catch (error) {
            console.log('error signing out: ', error);
        }
    };

    const handleSave = async ({ uploadedFile }) => {
        let reloadData = false;

        const newUsername = document.getElementById('profileNameChange').value;
        if (
            newUsername.length >= 3 &&
            newUsername.length <= 20 &&
            newUsername !== userData.profileName
        ) {
            updateUserInfo({ userId: userData.id, field: 'profileName', value: newUsername });
            reloadData = true;
        }

        if (uploadedFile) {
            const newUrl = `profilepictures/${userData.id}.jpg`;
            const params = {
                ContentType: uploadedFile.type,
                ACL: 'public-read',
                Body: uploadedFile,
                Bucket: S3_BUCKET,
                Key: newUrl,
            };
            myBucket.putObject(params).send((err) => {
                if (err) console.log(err);
            });
            reloadData = true;
        }

        if (reloadData) setRefreshTrigger((prev) => prev + 1);
        setEditOpen(false);
    };

    const hasUsername = userData.profileName && userData.profileName !== '';

    return (
        <div className="min-h-full w-full bg-base-100 py-10 px-4 flex flex-col items-center gap-6">

            {/* Edit modal */}
            {editOpen && (
                <EditModal
                    userData={userData}
                    onSave={handleSave}
                    onClose={() => setEditOpen(false)}
                />
            )}

            {/* Header card */}
            <div className="bg-base-200 rounded-2xl shadow p-8 w-full max-w-2xl flex flex-col sm:flex-row items-center gap-6">
                {/* Avatar */}
                <div className="avatar shrink-0">
                    <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                        <img src={userData.picture} alt="profile" />
                    </div>
                </div>

                {/* Info */}
                <div className="flex-1 flex flex-col items-center sm:items-start gap-2">
                    <div className="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
                        <h1 className="text-2xl font-bold text-base-content">
                            {hasUsername ? userData.profileName : 'No username set'}
                        </h1>
                        {!hasUsername && (
                            <span className="badge badge-warning badge-outline">Add a username!</span>
                        )}
                    </div>
                    <p className="text-base-content/50 text-sm">Chess Trainer</p>
                </div>

                {/* Edit button */}
                <button
                    className="btn btn-primary btn-sm shrink-0"
                    onClick={() => setEditOpen(true)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2a2 2 0 01.586-1.414z" />
                    </svg>
                    Edit Profile
                </button>
            </div>

            {/* Vision stats card */}
            <div className="bg-base-200 rounded-2xl shadow p-6 w-full max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-lg font-bold text-primary">Vision</h2>
                    <div className="divider divider-horizontal mx-0"></div>
                    <span className="text-base-content/40 text-sm">Board recognition training</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <StatCard label="Games Played" value={userStats.visionGamesPlayed} accent="primary" />
                    <StatCard label="Squares Clicked" value={userStats.totalSquaresClicked} accent="primary" />
                    <StatCard label="High Score" value={userStats.visionHighScore} accent="primary" />
                </div>
            </div>

            {/* Color Guesser stats card */}
            <div className="bg-base-200 rounded-2xl shadow p-6 w-full max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-lg font-bold text-secondary">Color Guesser</h2>
                    <div className="divider divider-horizontal mx-0"></div>
                    <span className="text-base-content/40 text-sm">Square color memorization</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <StatCard label="Games Completed" value={userStats.colorGuesserGamesPlayed} accent="secondary" />
                    <StatCard label="High Score" value={userStats.colorGuesserHighScore} accent="secondary" />
                </div>
            </div>

            {/* Performance charts */}
            <PerformanceSection userData={userData} />

            {/* Sign out */}
            <div className="w-full max-w-2xl flex justify-end pt-2">
                <button className="btn btn-error btn-sm btn-outline" onClick={handleSignout}>
                    Sign Out
                </button>
            </div>
        </div>
    );
}

export default Profile;
